import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Paper, FormControlLabel, Checkbox, Link } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { backendApiRequest } from '@/config/msal';

// Import logos
import boltLogo from '@/assets/Bolt.png';
import bossardMascot from '@/assets/Brainy-917.png';

// localStorage key for remembering terms acceptance
const TERMS_ACCEPTED_KEY = 'bolt_terms_accepted';

// No need for props anymore
const LoginPage: React.FC = () => {
  const { instance } = useMsal();
  // Check if user has previously accepted terms on this device/browser
  const [hasAcceptedBefore, setHasAcceptedBefore] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    // Check localStorage for previous acceptance
    const previouslyAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY) === 'true';
    setHasAcceptedBefore(previouslyAccepted);
    // If previously accepted, auto-check the checkbox
    if (previouslyAccepted) {
      setAcceptedTerms(true);
    }
  }, []);

  const handleTermsChange = (checked: boolean) => {
    setAcceptedTerms(checked);
    // Remember the acceptance in localStorage when user checks the box
    if (checked) {
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
    }
  };

  const handleLogin = () => {
    // Only proceed if terms are accepted
    if (acceptedTerms) {
      instance.loginRedirect({
        ...backendApiRequest,
        prompt: 'select_account',
      });
    }
  };

  return (
    <Container maxWidth="sm" sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      // textAlign: 'center'
    }}>
      <Paper
        elevation={0}
        sx={{
          // padding: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
          paddingX: 0,
          paddingY: 6,
          boxShadow: '8px 8px 22px rgba(0, 0, 0, 0.4)'
          // backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ width: '80%', marginBottom: 6, textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          {/* Bolt Logo */}
          <Box
            component="img"
            src={boltLogo}
            alt="BOLT Logo"
            sx={{
              width: '88%',
              height: 'auto',
              marginBottom: 2,
              marginLeft: 0
            }}
          />

          {/* Title */}
          <Box sx={{ width: '100%', marginLeft: 1 }}>
            <Typography variant="h6" component="h6" sx={{ fontWeight: 700, fontSize: '0.975rem', marginBottom: 0.25, color: '#000' }}>
              Bossard AI Platform
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 400, color: '#000' }}>
              Build Organize Learn & Transform in your daily operations
            </Typography>
          </Box>
        </Box>

        {/* Version number */}
        <Typography variant="caption" sx={{ fontSize: '0.975rem', fontWeight: 600, color: '#666', marginBottom: 5 }}>
          v20260522.04
        </Typography>

        {/* Legal Notice and Disclaimer - show checkbox for new users, or acceptance indicator for returning users */}
        {!hasAcceptedBefore ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => handleTermsChange(e.target.checked)}
                sx={{
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                  padding: '0px',
                  marginRight: '8px',
                  alignSelf: 'flex-start',
                  marginTop: '1px'
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                I have read and accept the{' '}
                <Link
                  href="https://bosnet.sharepoint.com/:w:/r/sites/Bossard_AI/AI%20related%20Documents/Legal%20Notice%20and%20Disclaimer.docx?d=wdddf2b40d49f45d586050b9cecb92384&csf=1&web=1&e=6KEcMM"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Legal Notice and Disclaimer
                </Link>
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              width: '50%',
              marginBottom: 15,
              // justifyContent: 'center',
              '& .MuiFormControlLabel-label': {
                paddingTop: '2px'
              }
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.5,
              marginBottom: 15,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <Box
              component="span"
              sx={{
                color: 'success.main',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '1rem'
              }}
            >
              ✓
            </Box>
            <Link
              href="https://bosnet.sharepoint.com/:w:/r/sites/Bossard_AI/AI%20related%20Documents/Legal%20Notice%20and%20Disclaimer.docx?d=wdddf2b40d49f45d586050b9cecb92384&csf=1&web=1&e=6KEcMM"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'text.secondary',
                textDecoration: 'underline',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
            >
              Terms accepted
            </Link>
          </Typography>
        )}

        {/* SSO Login Button with Bossard Mascot */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={!acceptedTerms}
            sx={{
              borderRadius: 8,
              paddingX: 4,
              paddingY: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              color: acceptedTerms ? '#212121' : '#9e9e9e',  // Conditional text color
              backgroundColor: acceptedTerms ? '#f5f5f5' : '#e0e0e0',  // Conditional background
              '&:hover': {
                backgroundColor: acceptedTerms ? '#e0e0e0' : '#e0e0e0',
                color: acceptedTerms ? '#000000' : '#9e9e9e',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Sign in with Microsoft
          </Button>

          {/* Bossard Mascot - positioned on the right */}
          <Box
            component="img"
            src={bossardMascot}
            alt="Bossard Mascot"
            sx={{
              width: 120,
              height: 'auto',
              position: 'absolute',
              right: 5,
              bottom: -5
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;